import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { FiArrowRight, FiCheckCircle, FiShoppingBag, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "../../Styles/QuickCartDrawer.css";
import { dismissToast } from "../../utils/Toast";
import { getCartRequest } from "../../Containers/Home/Home-slice";
import { openAuthModal } from "../../utils/authModal";
import { getBuyerToken } from "../../utils/authStorage";
import { getViewportIsMobile } from "../../utils/browser";
import OfferCountdown from "./OfferCountdown";
import {
  DEFAULT_PRODUCT_IMAGE,
  cleanNumber,
  getDisplayPrice,
  getDisplayPricing,
  getProductStockQuantity,
  isProductOutOfStock,
  withDefaultProductImage,
} from "../../utils/productPresentation";

const parseAttributes = (attributes) => {
  if (!attributes) return {};
  if (typeof attributes === "object" && !Array.isArray(attributes)) {
    return attributes;
  }

  if (typeof attributes !== "string") {
    return {};
  }

  try {
    const parsed = JSON.parse(attributes);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const getQuantity = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const getUnitLabel = (item, attributes) =>
  [
    item?.size_dosage,
    item?.unit,
    attributes?.size_dosage,
    attributes?.unit,
    attributes?.pack_qty ? `Pack ${attributes.pack_qty}` : "",
  ]
    .filter(Boolean)
    .join(" / ");

const getDescription = (item, attributes) => {
  const description =
    item?.short_description ||
    item?.description ||
    attributes?.short_description ||
    attributes?.description ||
    "";

  return String(description || "").trim();
};

const getOfferEndTime = (item) =>
  item?.offer_end_at ??
  item?.offerEndAt ??
  item?.active_offer?.offer_end_at ??
  item?.active_offer?.offerEndAt ??
  item?.active_offer?.to_date ??
  item?.active_offer?.toDate ??
  item?.to_date ??
  item?.toDate ??
  null;

const normalizeDrawerItem = (item) => {
  const attributes = parseAttributes(item?.attributes);
  const quantity = getQuantity(item?.quantity);
  const pricing = getDisplayPricing(item);
  const offerEndAt = getOfferEndTime(item);
  const isOfferActive =
    item?.is_offer_active === false || item?.isOfferActive === false
      ? false
      : Boolean((item?.is_offer_active ?? item?.isOfferActive ?? pricing.hasOffer) && offerEndAt);
  const originalPrice =
    pricing.originalPrice ||
    cleanNumber(item?.oldPrice ?? item?.old_price ?? getDisplayPrice(item));
  const unitPrice =
    pricing.finalPrice || cleanNumber(item?.finalPrice ?? getDisplayPrice(item)) || originalPrice;

  return {
    key: String(item?.product_id ?? item?.id ?? item?.title ?? item?.name ?? quantity),
    productId: item?.product_id ?? item?.id,
    name: item?.product_name || item?.title || item?.name || "Product",
    imageUrl:
      item?.product_image ||
      item?.imageUrl ||
      item?.image_url ||
      item?.singleImage ||
      item?.image,
    quantity,
    originalPrice,
    unitPrice,
    subtotal: unitPrice * quantity,
    hasOfferPricing: Boolean(pricing.hasOffer && isOfferActive),
    offerEndAt,
    isOfferActive,
    unitLabel: getUnitLabel(item, attributes),
    description: getDescription(item, attributes),
    isOutOfStock: isProductOutOfStock(item),
    availableStock: getProductStockQuantity(item),
  };
};

function QuickCartDrawer({ isOpen, onClose, product, priceLabel, imageUrl }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartData = useSelector((state) =>
    getBuyerToken() ? state.Home.getCartdata?.data : null,
  );
  const expiredOfferRefreshKeysRef = useRef(new Set());
  const offerRefreshInFlightRef = useRef(false);

  useEffect(() => {
    offerRefreshInFlightRef.current = false;
  }, [cartData]);

  const cartItems = useMemo(() => {
    const normalizedCartItems = Array.isArray(cartData)
      ? cartData.map((item) => normalizeDrawerItem(item))
      : [];

    const fallbackItem = normalizeDrawerItem({
      ...product,
      imageUrl: imageUrl || product?.imageUrl,
      price:
        cleanNumber(product?.finalPrice) ||
        getDisplayPrice(product) ||
        cleanNumber(priceLabel),
      product_image: imageUrl || product?.imageUrl,
    });

    const alreadyPresent = normalizedCartItems.some(
      (item) => String(item.productId) === String(fallbackItem.productId),
    );

    if (alreadyPresent || !fallbackItem.productId) {
      return normalizedCartItems;
    }

    return [fallbackItem, ...normalizedCartItems];
  }, [cartData, imageUrl, priceLabel, product]);

  const itemsCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + item.subtotal, 0);
  const totalAmount = subtotal;
  const highlightedProductId = String(product?.product_id ?? product?.id ?? "");

  const formatCurrency = (amount) =>
    `AED ${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleNavigate = (path, navigationState) => {
    onClose?.();
    if (navigationState) {
      navigate(path, { state: navigationState });
      return;
    }

    navigate(path);
  };

  const handleOfferCountdownExpire = (item) => {
    const refreshKey = `${item?.productId || item?.key}:${item?.offerEndAt || ""}`;

    if (
      !refreshKey ||
      expiredOfferRefreshKeysRef.current.has(refreshKey) ||
      offerRefreshInFlightRef.current
    ) {
      return;
    }

    expiredOfferRefreshKeysRef.current.add(refreshKey);
    offerRefreshInFlightRef.current = true;
    dispatch(getCartRequest());
  };

  const checkoutItems = cartItems.map((item) => ({
    id: item.productId,
    product_id: item.productId,
    quantity: item.quantity,
    qty: item.quantity,
  }));
  const productIdsString = checkoutItems
    .map((item) => item.product_id)
    .filter(Boolean)
    .join(",");

  const handleContinueShopping = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClose?.();
  };

  const handleViewCart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dismissToast();
    handleNavigate("/cart", { suppressCartMutationToasts: true });
  };

  const handleCheckout = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!checkoutItems.length || !productIdsString) {
      if (!getBuyerToken()) {
        openAuthModal({ returnTo: "/checkout" });
        return;
      }
      handleNavigate("/checkout");
      return;
    }

    onClose?.();

    if (!getBuyerToken()) {
      openAuthModal({ returnTo: "/checkout" });
      return;
    }

    navigate("/checkout", {
      state: {
        id: checkoutItems[0].product_id,
        productIdsString,
        cartItems: checkoutItems,
        totalQuantity: checkoutItems.reduce(
          (sum, item) => sum + (Number(item.quantity) || 0),
          0,
        ),
      },
    });
  };

  if (!isOpen || !product) return null;

  const drawer = (
    <>
      <div
        className="quick-cart-drawer__backdrop"
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
      />
      <aside
        className="quick-cart-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Quick add to cart"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="quick-cart-drawer__header">
          <div className="quick-cart-drawer__header-copy">
            <p className="quick-cart-drawer__eyebrow">Cart updated</p>
            <h3 className="quick-cart-drawer__title">Added to Cart</h3>
            <div className="quick-cart-drawer__status">
              <FiCheckCircle />
              <span>{itemsCount} item{itemsCount === 1 ? "" : "s"} ready</span>
            </div>
          </div>
          <button
            type="button"
            className="quick-cart-drawer__close"
            onClick={onClose}
            aria-label="Close cart drawer"
          >
            <FiX />
          </button>
        </div>

        <div className="quick-cart-drawer__body">
          {cartItems.map((item) => {
            const isHighlighted =
              highlightedProductId &&
              String(item.productId) === highlightedProductId;

            return (
              <article
                key={item.key}
                className={`quick-cart-drawer__item${
                  isHighlighted ? " is-highlighted" : ""
                }`}
              >
                <div className="quick-cart-drawer__media">
                  <img
                    src={withDefaultProductImage(item.imageUrl || imageUrl)}
                    alt={item.name}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                    }}
                  />
                </div>

                <div className="quick-cart-drawer__details">
                  <div className="quick-cart-drawer__item-top">
                    <h4>{item.name}</h4>
                    {isHighlighted ? (
                      <span className="quick-cart-drawer__item-badge">
                        Just added
                      </span>
                    ) : null}
                  </div>

                  <div className="quick-cart-drawer__meta-grid">
                    <span>Price</span>
                    <strong>{formatCurrency(item.unitPrice)}</strong>
                    <span>Quantity</span>
                    <strong>{item.quantity}</strong>
                    {item.unitLabel ? (
                      <>
                        <span>Unit</span>
                        <strong>{item.unitLabel}</strong>
                      </>
                    ) : null}
                    <span>Subtotal</span>
                    <strong>{formatCurrency(item.subtotal)}</strong>
                  </div>

                  {item.isOfferActive && item.offerEndAt ? (
                    <p
                      className="quick-cart-drawer__availability"
                      style={{ color: "#6b7280", marginTop: "8px" }}
                    >
                      <OfferCountdown
                        endTime={item.offerEndAt}
                        fallbackText=""
                        expiredText="Offer expired"
                        showZeroDays
                        onExpire={() => handleOfferCountdownExpire(item)}
                      />
                    </p>
                  ) : null}

                  {item.availableStock > 0 ? (
                    <p className="quick-cart-drawer__availability is-in-stock">
                      In stock: {item.availableStock}
                    </p>
                  ) : item.isOutOfStock ? (
                    <p className="quick-cart-drawer__availability is-out-of-stock">
                      Currently unavailable
                    </p>
                  ) : null}

                  {item.description ? (
                    <p className="quick-cart-drawer__description">
                      {item.description}
                    </p>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>

        <div className="quick-cart-drawer__footer">
          <div className="quick-cart-drawer__summary">
            <div className="quick-cart-drawer__summary-header">
              <FiShoppingBag />
              <span>Cart summary</span>
            </div>

            <div className="quick-cart-drawer__summary-row">
              <span>Items</span>
              <strong>{itemsCount}</strong>
            </div>
            <div className="quick-cart-drawer__summary-row">
              <span>Subtotal</span>
              <strong>{formatCurrency(subtotal)}</strong>
            </div>
            <div className="quick-cart-drawer__summary-row is-total">
              <span>Total</span>
              <strong>{formatCurrency(totalAmount)}</strong>
            </div>

            <span className="quick-cart-drawer__note">
              Quantity can be adjusted in the cart before checkout.
            </span>
          </div>

          <div className="quick-cart-drawer__actions">
            <button
              type="button"
              className="quick-cart-drawer__secondary"
              onClick={handleContinueShopping}
            >
              Continue Shopping
            </button>
            <button
              type="button"
              className="quick-cart-drawer__ghost"
              onClick={handleViewCart}
            >
              View Cart
              <FiArrowRight />
            </button>
            <button
              type="button"
              className="quick-cart-drawer__primary"
              onClick={handleCheckout}
            >
              Checkout
              <FiArrowRight />
            </button>
          </div>
        </div>
      </aside>
    </>
  );

  return getViewportIsMobile() ? createPortal(drawer, document.body) : drawer;
}

export default QuickCartDrawer;
